import { toBoolean } from "./misc";

export const INSIGHT_TAB_FLAGS = {
  overview: "overviewEnabled",
  leakage: "leakageEnabled",
  channels: "channelsEnabled",
  staff: "staffEnabled",
  network: "networkEnabled",
  arrivals: "arrivalsEnabled",
  ops: "dataInputEnabled",
};

export const INSIGHTS_ACCESS_FLAGS = [
  "insightsModuleEnabled",
  "overviewEnabled",
  "dataInputEnabled",
  "staffEnabled",
  "leakageEnabled",
  "networkEnabled",
  "arrivalsEnabled",
  "channelsEnabled",
];

export const DEFAULT_INSIGHTS_CONFIGURATION = {
  overview: {
    budgetTracker: {
      revenuePerMonth: 40000,
      revenuePerMonthLocked: false,
      buzzOutsPerDay: 4.5,
      buzzOutsPerDayLocked: false,
      focCardsPerDay: 8.8,
      focCardsPerDayLocked: false,
    },
    playbookTargets: {
      drivoChargeRatePercent: 95,
      maxFocCardsPerDay: 8.8,
      maxBuzzOutsPerDay: 4.5,
      minParkingOccupancyPercent: 75,
    },
  },
  dataInput: {
    property: {
      totalRooms: 180,
      totalRoomsLocked: false,
      totalParkingBays: 100,
      totalParkingBaysLocked: false,
    },
    revenueAssumptions: {
      defaultAdr: 320,
      fbRevenuePerOccupiedRoom: 45,
      otherRevenuePerOccupiedRoom: 12,
      capRatePercent: 6.5,
    },
    sanctionedPlateOpportunityCost: {
      standardHourlyRate: 22,
      standardHourlyRateLocked: false,
      chargeableRatio: 0.7,
    },
    nightAudit: {
      enabled: true,
      reminderEnabled: true,
      reminderTimeLocal: "00:00",
      reminderEmail: "",
    },
  },
};

function parseConfiguration(configuration) {
  if (!configuration) return null;

  if (typeof configuration === "string") {
    try {
      return JSON.parse(configuration);
    } catch {
      return null;
    }
  }

  if (typeof configuration === "object") return configuration;

  return null;
}

export function getInsightsConfigurationRecord(facility) {
  return facility?.featureFacilityProviderConfigurations?.find(
    (item) => item?.feature?.name === "INSIGHTS",
  );
}

export function getInsightsConfiguration(facility) {
  const config = getInsightsConfigurationRecord(facility);
  const configuration = parseConfiguration(config?.configuration);

  if (!configuration || Object.keys(configuration).length === 0) return null;

  return configuration;
}

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;

  return Object.keys(override).reduce((acc, key) => {
    const value = override[key];

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      acc[key] &&
      typeof acc[key] === "object" &&
      !Array.isArray(acc[key])
    ) {
      acc[key] = deepMerge(acc[key], value);
    } else {
      acc[key] = value;
    }

    return acc;
  }, { ...base });
}

export function buildInsightsConfiguration(currentConfiguration, patch = {}) {
  const accessFlags = INSIGHTS_ACCESS_FLAGS.reduce((acc, flag) => {
    if (Object.prototype.hasOwnProperty.call(currentConfiguration || {}, flag)) {
      acc[flag] = currentConfiguration[flag];
    }
    return acc;
  }, {});

  return {
    ...deepMerge(
      {
        ...DEFAULT_INSIGHTS_CONFIGURATION,
        ...accessFlags,
      },
      deepMerge(currentConfiguration || {}, patch),
    ),
    ...accessFlags,
  };
}

export function isInsightsModuleEnabled(facility) {
  const configuration = getInsightsConfiguration(facility);

  return toBoolean(configuration?.insightsModuleEnabled);
}

export function isInsightTabEnabled(facility, tab) {
  const configuration = getInsightsConfiguration(facility);
  const flag = INSIGHT_TAB_FLAGS[tab];

  return isInsightsModuleEnabled(facility) && toBoolean(configuration?.[flag]);
}

export function getEnabledInsightTabs(facility) {
  return Object.keys(INSIGHT_TAB_FLAGS).filter((tab) =>
    isInsightTabEnabled(facility, tab),
  );
}
